import React, { forwardRef, PropsWithChildren } from 'react'

export type BoxVariant =
  | 'border'
  | 'border-shade'
  | 'solid'
  | 'solid-warm'
  | 'solid-neutral'

export interface BoxProps {
  additionalClassName?: string
  as?: keyof JSX.IntrinsicElements
  link?: string
  scroll?: boolean
  variant: BoxVariant
  paddingStyle?: 'default' | 'custom'
  padding?: string
  /**
   * Disables "group" to avoid inner group-hover and group-focus to trigger
   * when hovering inside the box
   */
  disableGroupHover?: boolean
}

export const Box = forwardRef<keyof JSX.IntrinsicElements, PropsWithChildren<BoxProps>>(
  (
    {
      additionalClassName,
      as: Root = 'div',
      children,
      link,
      padding: customPadding = '',
      paddingStyle = 'default',
      scroll,
      variant = 'border',
      disableGroupHover = false,
    },
    ref
  ) => {
    const defaultPadding = 'w-full p-30 tb:p-45'

    // transform-gpu fixes border which is not visible on left side on iphone
    const defaultClasses = 'relative box-border rounded transform-gpu'
    let className = disableGroupHover ? defaultClasses : `group ${defaultClasses}`
    let padding: string

    switch (paddingStyle) {
      case 'custom':
        padding = customPadding
        break
      default:
      case 'default':
        padding = defaultPadding
        break
    }

    switch (variant) {
      case 'border':
        className += ' bg-neutral border-1/2 border-primary'
        break

      case 'border-shade':
        className += ' bg-neutral border-1/2 border-shade'
        break

      case 'solid':
        className += ' bg-shade-lightest'
        break

      case 'solid-warm':
        className += ' bg-shade-lightest-warm'
        break

      case 'solid-neutral':
        className += ' bg-neutral'
        break
    }

    if (scroll) {
      className += ' overflow-y-auto'
    }

    if (additionalClassName) {
      className += ` ${additionalClassName}`
    }

    let LinkWrapper: React.ElementType = React.Fragment
    let linkWrapperProps: React.AnchorHTMLAttributes<HTMLAnchorElement> = {}
    if (link) {
      LinkWrapper = 'a'
      linkWrapperProps = {
        className: `${padding} h-full relative block`,
        href: link,
      }
    } else {
      className += ` ${padding}`
    }

    return (
      // @ts-ignore: Typescript complains when we try to set the ref here on Root.
      // Casting the type to any outputs the rather unhelpful "Expression produces a union type that is too complex to represent.", so just ignore the ts error.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      <Root className={className} ref={ref as unknown as any}>
        <LinkWrapper {...linkWrapperProps}>{children}</LinkWrapper>
      </Root>
    )
  }
)
